"use client";
import React, { useState, useEffect, useRef } from 'react';

interface StackCarouselProps {
  images: string[];
  autoRotate?: boolean;
  autoRotateInterval?: number;
  className?: string;
}

export const StackCarousel: React.FC<StackCarouselProps> = ({
  images,
  autoRotate = true,
  autoRotateInterval = 4000,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const totalImages = images.length;

  useEffect(() => {
    if (autoRotate && !isHovered) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % totalImages);
      }, autoRotateInterval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRotate, autoRotateInterval, isHovered, totalImages]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  };

  const getImagePosition = (index: number) => {
    const offset = index - currentIndex;
    
    // Normalize offset to handle circular array
    let normalizedOffset = offset;
    if (normalizedOffset > totalImages / 2) {
      normalizedOffset -= totalImages;
    } else if (normalizedOffset < -totalImages / 2) {
      normalizedOffset += totalImages;
    }

    // Responsive offsets based on viewport width
    // For mobile: smaller offsets, for desktop: larger offsets
    const getResponsiveOffset = (baseOffset: number) => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 640) {
          // Mobile: 40% of base
          return baseOffset * 0.3;
        } else if (width < 768) {
          // Small tablet: 60% of base
          return baseOffset * 0.6;
        } else if (width < 1024) {
          // Tablet: 80% of base
          return baseOffset * 0.8;
        }
        // Desktop: full base
        return baseOffset;
      }
      return baseOffset;
    };

    if (normalizedOffset === 0) {
      // Center (front) image
      return {
        x: 0,
        z: 0,
        scale: 1,
        opacity: 1,
        zIndex: 10,
        blur: 0,
      };
    } else if (normalizedOffset === -1) {
      // Left side (behind, left half visible)
      return {
        x: getResponsiveOffset(-200),
        z: -50,
        scale: 0.92,
        opacity: 0.75,
        zIndex: 5,
        blur: 3,
      };
    } else if (normalizedOffset === 1) {
      // Right side (behind, right half visible)
      return {
        x: getResponsiveOffset(200),
        z: -50,
        scale: 0.92,
        opacity: 0.75,
        zIndex: 5,
        blur: 3,
      };
    } else {
      // Hidden images - only show current, left, and right
      return {
        x: normalizedOffset > 0 ? getResponsiveOffset(500) : getResponsiveOffset(-500),
        z: -150,
        scale: 0.5,
        opacity: 0,
        zIndex: 0,
        blur: 3,
      };
    }
  };

  return (
    <div
      className={`relative w-full flex items-center justify-center h-[20rem] sm:h-[28rem] md:h-[35rem] lg:h-[40rem] ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Container with 3D perspective */}
      <div
        className="relative w-full flex items-center justify-center"
        style={{
          perspective: '1000px',
          perspectiveOrigin: '50% 50%',
        }}
      >
        <div className="relative w-full flex items-center justify-center" style={{ margin: '0 auto' }}>
          {/* Render visible images (current, left, and right only) */}
          {images.map((image, index) => {
            const position = getImagePosition(index);
            
            // Only render if visible (opacity > 0)
            if (position.opacity === 0) {
              return null;
            }

            const isActive = index === currentIndex;
            const isLeft = position.x < 0;
            const isRight = position.x > 0;
            const isClickable = !isActive && (isLeft || isRight);

            const handleImageClick = () => {
              if (isClickable) {
                if (isLeft) {
                  // Click on left image - go to previous
                  handlePrevious();
                } else if (isRight) {
                  // Click on right image - go to next
                  handleNext();
                }
              }
            };

            return (
              <div
                key={`${image}-${index}`}
                className={`absolute w-[280px] sm:w-[400px] md:w-[500px] lg:w-[600px] xl:w-[60rem] transition-all duration-200 ease-out ${
                  isClickable ? 'cursor-pointer md:hover:scale-105 md:active:scale-95' : 'cursor-default'
                }`}
                style={{
                  left: '50%',
                  top: '70%',
                  transform: `translate3d(calc(-50% + ${position.x}px), -50%, ${position.z}px) scale(${position.scale})`,
                  opacity: position.opacity,
                  zIndex: position.zIndex,
                  transformStyle: 'preserve-3d',
                  filter: `blur(${position.blur}px)`,
                  transition: 'transform 0.2s, opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), filter 0.2s ease-out',
                }}
                onClick={handleImageClick}
                onTouchStart={(e) => {
                  // Prevent default to allow click handling on mobile
                  if (isClickable) {
                    e.stopPropagation();
                  }
                }}
              >
                <div className="relative w-full rounded-xl sm:rounded-2xl overflow-hidden border border-white/20 bg-white/5 backdrop-blur-xl shadow-2xl p-2 sm:p-3 md:p-4">
                  <img
                    src={image}
                    alt={`Module ${index + 1}`}
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none rounded-xl sm:rounded-2xl" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

